'use client';

import {
  useState, useEffect, useCallback, useRef,
} from 'react';
import { useReduxUser } from '@hooks/common/useAuthClient/useReduxUser';

const MC_CONTEXT_KEY = 'mc_context';
const MC_CONTEXT_EVENT = 'mc-context-change';

export type MissionControlContext = 'candidate' | 'company';

interface UseMissionControlContextOptions {
  /**
   * Server-derived initial context (from session token on first render).
   * Prevents the candidate->company flash for company users.
   * Only used when there is no explicit localStorage preference.
   */
  initialContext?: MissionControlContext;
}

interface UseMissionControlContextReturn {
  currentContext: MissionControlContext;
  switchContext: () => void;
  setContext: (nextContext: MissionControlContext) => void;
  clearContext: () => void;
}

/**
 * @description Hook for managing Mission Control context (candidate vs company view).
 *
 * Priority order:
 * 1. Explicit localStorage preference (user has manually switched at least once).
 * 2. `initialContext` prop (server-derived from session token — no flash, no extra fetch).
 * 3. Live `headerInfo.hasBusinessWallet` from Redux (catches late-loading headerInfo).
 * 4. Fallback: 'candidate'.
 */
export const useMissionControlContext = ({ initialContext }: UseMissionControlContextOptions = {}): UseMissionControlContextReturn => {
  const { headerInfo } = useReduxUser();
  const isCompany = !!(headerInfo as ApiSchema<'HeaderInfo2'> | undefined)?.hasBusinessWallet;

  // Only explicit user actions should persist and override server/runtime defaults.
  const hasExplicitPreferenceRef = useRef(false);

  const dispatchContextChange = useCallback((nextContext: MissionControlContext) => {
    if (typeof window === 'undefined') return;

    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent<MissionControlContext>(MC_CONTEXT_EVENT, { detail: nextContext }));
    }, 0);
  }, []);

  const persistExplicitPreference = useCallback((nextContext: MissionControlContext) => {
    hasExplicitPreferenceRef.current = true;

    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(MC_CONTEXT_KEY, nextContext);
    } catch (error) {
      console.warn('Failed to save mission control context to localStorage:', error);
    }
  }, []);

  // Keep first render deterministic across server and client.
  // Client-only sources such as localStorage are applied after mount.
  const [currentContext, setCurrentContext] = useState<MissionControlContext>(initialContext ?? 'candidate');

  // After hydration, apply explicit localStorage preference (user has manually switched before).
  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const stored = localStorage.getItem(MC_CONTEXT_KEY);
      if (stored === 'candidate' || stored === 'company') {
        hasExplicitPreferenceRef.current = true;
        setCurrentContext(stored);
      }
    } catch {
      // ignore storage read failures and fall back to derived values
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (hasExplicitPreferenceRef.current) return;

    if (initialContext) {
      setCurrentContext(initialContext);
      return;
    }

    setCurrentContext(isCompany ? 'company' : 'candidate');
  }, [initialContext, isCompany]);

  // Listen for context changes dispatched by other hook instances (e.g. header vs MissionControl).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleExternalSwitch = (event: Event) => {
      const nextContext = (event as CustomEvent<MissionControlContext>).detail;
      if (nextContext === 'candidate' || nextContext === 'company') {
        setCurrentContext(nextContext);
      }
    };

    window.addEventListener(MC_CONTEXT_EVENT, handleExternalSwitch);
    return () => window.removeEventListener(MC_CONTEXT_EVENT, handleExternalSwitch);
  }, []);

  const setContext = useCallback((nextContext: MissionControlContext) => {
    persistExplicitPreference(nextContext);
    setCurrentContext(nextContext);
    dispatchContextChange(nextContext);
  }, [dispatchContextChange, persistExplicitPreference]);

  const switchContext = useCallback(() => {
    setCurrentContext((prevContext) => {
      const nextContext: MissionControlContext = prevContext === 'candidate' ? 'company' : 'candidate';

      persistExplicitPreference(nextContext);
      dispatchContextChange(nextContext);

      return nextContext;
    });
  }, [dispatchContextChange, persistExplicitPreference]);

  const clearContext = useCallback(() => {
    hasExplicitPreferenceRef.current = false;
    setCurrentContext('candidate');
    dispatchContextChange('candidate');

    if (typeof window === 'undefined') return;

    try {
      localStorage.removeItem(MC_CONTEXT_KEY);
    } catch (error) {
      console.warn('Failed to clear mission control context from localStorage:', error);
    }
  }, [dispatchContextChange]);

  return {
    currentContext,
    switchContext,
    setContext,
    clearContext,
  };
};
